const DEFAULT_OPTIONS = {
  invoiceIdKey: 'invoice_id',
  simpleNoteIdKey: 'simple_note_id',
  simpleNoteReferenceKey: 'simple_note_reference',
  invoiceReferenceKeys: ['invoice_reference', 'invoice'],
};

const toText = (value) => String(value || '').trim();

const normalizeOptions = (options = {}) => {
  const invoiceReferenceKeys = Array.isArray(options?.invoiceReferenceKeys)
    ? options.invoiceReferenceKeys.map((key) => toText(key)).filter((key) => key !== '')
    : [];

  return {
    invoiceIdKey: toText(options?.invoiceIdKey) || DEFAULT_OPTIONS.invoiceIdKey,
    simpleNoteIdKey: toText(options?.simpleNoteIdKey) || DEFAULT_OPTIONS.simpleNoteIdKey,
    simpleNoteReferenceKey: toText(options?.simpleNoteReferenceKey) || DEFAULT_OPTIONS.simpleNoteReferenceKey,
    invoiceReferenceKeys: invoiceReferenceKeys.length > 0
      ? invoiceReferenceKeys
      : DEFAULT_OPTIONS.invoiceReferenceKeys,
  };
};

export const hasSimpleNoteVoucherDocument = (row = {}, options = {}) => {
  const config = normalizeOptions(options);
  const simpleNoteId = Number(row?.[config.simpleNoteIdKey] || 0);
  const simpleNoteReference = toText(row?.[config.simpleNoteReferenceKey]);

  return simpleNoteId > 0 || simpleNoteReference !== '';
};

export const normalizePaymentVoucherType = (row = {}, options = {}) => {
  if (Number(row?.special || 0) === 1) {
    return 'simple_note';
  }

  const voucherType = toText(row?.voucher_type).toLowerCase();
  if (voucherType !== '') {
    return voucherType === 'simple_note' ? 'simple_note' : 'cfdi';
  }

  return hasSimpleNoteVoucherDocument(row, options) ? 'simple_note' : 'cfdi';
};

export const isSimpleNotePaymentVoucher = (row = {}, options = {}) => {
  return normalizePaymentVoucherType(row, options) === 'simple_note';
};

export const resolvePaymentInvoiceId = (row = {}, options = {}) => {
  const config = normalizeOptions(options);
  return Number(row?.[config.invoiceIdKey] || 0);
};

export const resolvePaymentInvoiceReference = (row = {}, options = {}) => {
  const config = normalizeOptions(options);

  for (const key of config.invoiceReferenceKeys) {
    const reference = toText(row?.[key]);
    if (reference !== '') {
      return reference;
    }
  }

  return '';
};

export const hasPaymentInvoiceDocument = (row = {}, options = {}) => {
  return resolvePaymentInvoiceId(row, options) > 0
    || resolvePaymentInvoiceReference(row, options) !== '';
};

export const canOpenPaymentInvoiceDetail = (row = {}, options = {}) => {
  return !isSimpleNotePaymentVoucher(row, options) && resolvePaymentInvoiceId(row, options) > 0;
};

export const resolvePaymentBillingReference = (row = {}, options = {}) => {
  const config = normalizeOptions(options);

  if (isSimpleNotePaymentVoucher(row, options)) {
    return toText(row?.[config.simpleNoteReferenceKey]) || resolvePaymentInvoiceReference(row, options);
  }

  return resolvePaymentInvoiceReference(row, options);
};

export const canIssuePaymentInvoice = (row = {}, { isPaid = false, ...options } = {}) => {
  return !isSimpleNotePaymentVoucher(row, options)
    && !hasPaymentInvoiceDocument(row, options)
    && !Boolean(isPaid);
};

export const canIssuePaymentSimpleNote = (row = {}, options = {}) => {
  return isSimpleNotePaymentVoucher(row, options)
    && !hasSimpleNoteVoucherDocument(row, options);
};

export const canShowPayActionButton = (row = {}, { hasPayment = false, ...options } = {}) => {
  if (Boolean(hasPayment)) {
    return false;
  }

  if (isSimpleNotePaymentVoucher(row, options)) {
    return true;
  }

  return hasPaymentInvoiceDocument(row, options);
};